import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  HStack,
  VStack,
  Text,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  Select,
  Badge,
} from "@chakra-ui/react";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineDocumentDuplicate,
} from "react-icons/hi";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import Navbar from "./Navbar";
import { useAuth } from "./AuthContext";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import DocumentDefinitionSelectById from "./DocumentDefinitionSelectById";
import HeroImage from "./images/stamps.png";

ModuleRegistry.registerModules([AllCommunityModule]);

const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
const DEFINITIONS_API = "https://documents.poc.mrc.ai/api/definitions";

export default function StudioHomepage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { get, post, delete: del, getHeaders, isAuthenticated } = useAuthenticatedRequest();
  const { isOpen, onOpen, onClose } = useDisclosure();


  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [creating, setCreating] = useState(false);
  const [documentDefinitions, setDocumentDefinitions] = useState({});
  const gridRef = useRef(null);

  // New document form state
  const [newDocName, setNewDocName] = useState("");
  const [newDocDefinitionId, setNewDocDefinitionId] = useState(null);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState(null);
  const [availableOrganisations, setAvailableOrganisations] = useState([]);
  const [organisationNames, setOrganisationNames] = useState({});
  const [documentOrgNames, setDocumentOrgNames] = useState({}); // Cache for document org names

  // Fetch all documents
  useEffect(() => {
    if (isAuthenticated) {
      fetchDocuments();
      fetchDocumentDefinitions();
    }
  }, [isAuthenticated]);

  // Fetch when page, pageSize, or searchTerm changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchDocuments(currentPage, pageSize, searchTerm);
    }
  }, [currentPage, pageSize, searchTerm, isAuthenticated]);

  const fetchDocumentDefinitions = async () => {
    try {
      const response = await get(DEFINITIONS_API);
      const data = response.data;

      // Create a map of definitionId -> definition name
      const definitionsMap = {};
      if (Array.isArray(data)) {
        data.forEach(def => {
          if (def._id) {
            definitionsMap[def._id] = def.name || "Unnamed Definition";
          }
        });
      }
      setDocumentDefinitions(definitionsMap);
    } catch (err) {
      toast({
        title: "Error loading document definitions",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const fetchOrganizationNamesForDocuments = async (docs) => {
    if (!docs || docs.length === 0) return;

    const PARTIES_API = "https://directory.poc.mrc.ai/api/parties";
    const orgNamesMap = { ...documentOrgNames };

    // Get unique org IDs that we haven't fetched yet
    const uniqueOrgIds = [...new Set(
      docs
        .map(doc => doc.organisationOwnerId)
        .filter(id => id && !orgNamesMap[id])
    )];

    if (uniqueOrgIds.length === 0) return;

    // Fetch all organization names in parallel
    const namePromises = uniqueOrgIds.map(async (orgId) => {
      try {
        const response = await get(`${PARTIES_API}/${orgId}`);
        orgNamesMap[orgId] = response.data.name || response.data.legalName || orgId;
      } catch (err) {
        orgNamesMap[orgId] = orgId; // Fallback to ID if fetch fails
      }
    });

    await Promise.all(namePromises);
    setDocumentOrgNames(orgNamesMap);
  };

  const fetchDocuments = async (page = currentPage, size = pageSize, search = searchTerm) => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: size.toString(),
        sortBy: "updatedDate",
        sortDescending: "true"
      });

      if (search) {
        queryParams.append("search", search);
      }

      const response = await get(`${DOCS_API}?${queryParams}`);
      const data = response.data;

      const items = data.items || [];
      setDocuments(items);
      setTotalCount(data.pagination?.totalCount || 0);

      // Fetch organization names for all documents
      await fetchOrganizationNamesForDocuments(items);
    } catch (err) {
      // Handle 401 Unauthorized errors
      if (err.response?.status === 401) {
        toast({
          title: "Authentication expired",
          description: "Your session has expired. Please log out and log back in.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error loading documents",
          description: err.response?.data?.message || err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = async () => {
    setNewDocName("");
    setNewDocDefinitionId(null);

    // Extract unique organization IDs from JWT
    let orgIds = [];
    if (user?.idToken) {
      try {
        const parts = user.idToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const rawOrgIds = payload.organisationId;
          if (Array.isArray(rawOrgIds)) {
            // Get unique organization IDs
            orgIds = [...new Set(rawOrgIds)];
          } else if (typeof rawOrgIds === 'string') {
            orgIds = [rawOrgIds];
          }
        }
      } catch (err) {
      }
    }

    setAvailableOrganisations(orgIds);
    // Auto-select first org if only one available
    setSelectedOrganisationId(orgIds.length === 1 ? orgIds[0] : null);

    // Fetch organization names for each unique org ID
    if (orgIds.length > 0) {
      try {
        const PARTIES_API = "https://directory.poc.mrc.ai/api/parties";
        const orgNamesMap = {};

        // Fetch all organization names in parallel
        const namePromises = orgIds.map(async (orgId) => {
          try {
            const response = await get(`${PARTIES_API}/${orgId}`);
            orgNamesMap[orgId] = response.data.name || response.data.legalName || orgId;
          } catch (err) {
            orgNamesMap[orgId] = orgId; // Fallback to ID if fetch fails
          }
        });

        await Promise.all(namePromises);
        setOrganisationNames(orgNamesMap);
      } catch (err) {
      }
    }

    onOpen();
  };

  const handleCreateDocument = async () => {
    if (!newDocDefinitionId) {
      toast({
        title: "Document Definition Required",
        description: "Please select a document definition",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!newDocName.trim()) {
      toast({
        title: "Document Name Required",
        description: "Please enter a document name",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedOrganisationId) {
      toast({
        title: "Organization Required",
        description: "Please select an organization for this document",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);

      const userId = user?._id ||
                     user?.platformUser?._id ||
                     user?.platformUser?.id ||
                     user?.linkedInId ||
                     user?.sub ||
                     user?.email ||
                     null;

      const payload = {
        definitionId: newDocDefinitionId,
        name: newDocName.trim(),
        createdBy: userId,
        organisationOwnerId: selectedOrganisationId,
      };

      const response = await post(DOCS_API, payload);
      const created = response.data;

      toast({
        title: "Document created",
        status: "success",
        duration: 2000,
      });

      onClose();

      // Small delay to ensure document is persisted before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate to the editor
      navigate(`/editor?doc=${created._id}`);
    } catch (err) {

      // Check for 401 unauthorized
      if (err.response?.status === 401) {
        toast({
          title: "Session Expired",
          description: "Your session has expired. Please refresh the page to log in again.",
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error creating document",
          description: err.response?.data?.message || err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEditDocument = (docId) => {
    navigate(`/editor?doc=${docId}`);
  };

  const handleCopyDocument = async (docId) => {
    try {
      const userId = user?._id ||
                     user?.platformUser?._id ||
                     user?.platformUser?.id ||
                     user?.linkedInId ||
                     user?.sub ||
                     user?.email ||
                     null;

      const payload = {
        createdBy: userId,
      };

      const response = await post(`${DOCS_API}/${docId}/copy`, payload);
      const copiedDoc = response.data;

      toast({
        title: "Document copied",
        status: "success",
        duration: 2000,
      });

      // Small delay to ensure document is persisted before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate to the newly copied document
      navigate(`/editor?doc=${copiedDoc._id}`);
    } catch (err) {
      toast({
        title: "Error copying document",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await del(`${DOCS_API}/${docId}`);

      toast({
        title: "Document deleted",
        status: "success",
        duration: 2000,
      });

      // Refresh the list
      fetchDocuments();
    } catch (err) {
      // Handle public document error specifically
      if (err.response?.status === 403 && err.response?.data?.isPublic) {
        toast({
          title: "Cannot delete public document",
          description: "Public documents cannot be deleted. Please contact an administrator if you need to remove this document.",
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error deleting document",
          description: err.response?.data?.error || err.response?.data?.message || err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "—";
    }
  };

  // AG Grid cell renderers
  const EditCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Edit Document">
          <IconButton
            icon={<HiOutlinePencil />}
            size="sm"
            onClick={() => handleEditDocument(params.data._id)}
            aria-label="Edit Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const CopyCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Copy Document">
          <IconButton
            icon={<HiOutlineDocumentDuplicate />}
            size="sm"
            colorScheme="teal"
            variant="ghost"
            onClick={() => handleCopyDocument(params.data._id)}
            aria-label="Copy Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const OrganizationOwnerCellRenderer = (params) => {
    const orgId = params.data?.organisationOwnerId;
    
    if (!orgId) {
      return (
        <Box display="flex" alignItems="center" height="100%">
          <Badge colorScheme="blue">PUBLIC</Badge>
        </Box>
      );
    }

    const orgName = documentOrgNames[orgId] || orgId;
    return (
      <Box display="flex" alignItems="center" height="100%">
        <Text>{orgName}</Text>
      </Box>
    );
  };

  const DeleteCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Delete Document">
          <IconButton
            icon={<HiOutlineTrash />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => handleDeleteDocument(params.data._id)}
            aria-label="Delete Document"
          />
        </Tooltip>
      </Box>
    );
  };

  const DateCellRenderer = (params) => {
    return formatDate(params.value);
  };

  const DefinitionCellRenderer = (params) => {
    return documentDefinitions[params.value] || "—";
  };

  // AG Grid column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "edit",
        cellRenderer: EditCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
        pinned: "left",
      },
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.name || "Untitled",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Document Definition",
        field: "definitionId",
        flex: 2,
        sortable: true,
        filter: true,
        cellRenderer: DefinitionCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Product Code",
        field: "productCode",
        flex: 1,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.productCode || "—",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Organization",
        field: "organisationOwnerId",
        flex: 1.5,
        sortable: true,
        filter: true,
        cellRenderer: OrganizationOwnerCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Template",
        field: "isTemplate",
        width: 100,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.isTemplate ? "Yes" : "No",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Last Updated",
        field: "updatedDate",
        flex: 1,
        sortable: true,
        filter: "agDateColumnFilter",
        cellRenderer: DateCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.status || "—",
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "copy",
        cellRenderer: CopyCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
      },
    ],
    [documentDefinitions, documentOrgNames]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
    }),
    []
  );

  // Note: gridRef is already declared at the top of the component

  if (!isAuthenticated) {
    return (
      <>
        <Navbar />
        <Center height="calc(100vh - 80px)">
          <VStack spacing={4}>
            <Text fontSize="xl" color="gray.600">
              Please log in to view documents
            </Text>
          </VStack>
        </Center>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <Center height="calc(100vh - 80px)">
          <Spinner size="xl" />
        </Center>
      </>
    );
  }

  return (
    <Box position="relative" minHeight="100vh">
      <Navbar />

      {/* Background image */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        zIndex={-1}
        pointerEvents="none"
      >
        <Box
          as="img"
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          filter="blur(1px)"
        />
      </Box>

      <Box p={8} maxWidth="1400px" mx="auto" position="relative" zIndex={1}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="xl">Studio Documents</Heading>
            <Button
              leftIcon={<HiOutlinePlus />}
              colorScheme="blue"
              onClick={handleOpenCreateModal}
            >
              Create
            </Button>
          </HStack>

          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <HiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {documents.length === 0 ? (
            <Box
              borderWidth="1px"
              borderRadius="md"
              overflow="hidden"
              backgroundColor="white"
            >
              <Center p={8}>
                <VStack spacing={4}>
                  <Text color="gray.500" fontSize="lg">
                    No documents yet. Create your first document!
                  </Text>
                  <Button
                    leftIcon={<HiOutlinePlus />}
                    colorScheme="blue"
                    onClick={handleOpenCreateModal}
                  >
                    Create
                  </Button>
                </VStack>
              </Center>
            </Box>
          ) : (
            <>
              <Box
                className="ag-theme-quartz"
                style={{ height: "calc(100vh - 380px)", width: "100%" }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={documents}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={50}
                  pagination={false}
                  animateRows={true}
                  rowSelection="single"
                  getRowId={(params) => params.data._id}
                />
              </Box>

              {/* Custom Pagination Controls */}
              <Box
                mt={4}
                p={4}
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <HStack justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      isDisabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      isDisabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(p => p + 1)}
                      isDisabled={currentPage >= Math.ceil(totalCount / pageSize)}
                    >
                      Next
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                      isDisabled={currentPage >= Math.ceil(totalCount / pageSize)}
                    >
                      Last
                    </Button>
                  </HStack>
                  <HStack spacing={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
                      {' '}({totalCount} total)
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm">Items per page:</Text>
                      <Select
                        size="sm"
                        w="80px"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </Select>
                    </HStack>
                  </HStack>
                </HStack>
              </Box>
            </>
          )}
        </VStack>
      </Box>

      {/* Create Document Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Document Name</FormLabel>
                <Input
                  placeholder="Enter document name..."
                  value={newDocName}
                  onChange={(e) => setNewDocName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleCreateDocument();
                    }
                  }}
                />
              </FormControl>

              <DocumentDefinitionSelectById
                label="Document Definition"
                selectedDocumentDefinitionId={newDocDefinitionId}
                onChange={(id) => setNewDocDefinitionId(id)}
              />

              {availableOrganisations.length > 1 && (
                <FormControl isRequired>
                  <FormLabel>Organization</FormLabel>
                  <Select
                    placeholder="Select organization..."
                    value={selectedOrganisationId || ""}
                    onChange={(e) => setSelectedOrganisationId(e.target.value)}
                  >
                    {availableOrganisations.map((orgId) => (
                      <option key={orgId} value={orgId}>
                        {organisationNames[orgId] || orgId}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={creating}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateDocument}
              isLoading={creating}
              loadingText="Creating..."
            >
              Create Document
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
